import { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { exportDbSnapshot, importDbSnapshot } from '../db/client';
import { createEncryptedBackup, decryptEncryptedBackup } from '../lib/backup';
import {
  billingConfigured,
  getBillingDiagnostics,
  getCachedEntitlement,
  getSubscriptionManagementUrl,
  listAvailablePlans,
  purchasePlan,
  refreshEntitlementFromStore,
  restoreEntitlementsFromStore,
  type BillingDiagnostics,
  type EntitlementState,
  type MonetizationPlan
} from '../lib/monetization';
import { appendMonetizationEvent, clearMonetizationEvents } from '../lib/monetizationEvents';
import {
  connectGoogleDrive,
  disconnectGoogleDrive,
  downloadLatestBackupFromGoogleDrive,
  hasGoogleDriveClientId,
  isGoogleDriveConnected,
  uploadBackupToGoogleDrive
} from '../lib/googleDrive';
import { downloadBlob } from '../lib/export';

type Translate = (key: string, vars?: Record<string, string | number>) => string;

type UseOptionsControllerParams = {
  tab: string;
  tr: Translate;
  openUrl: (url: string) => Promise<void>;
  onAfterImportRestore: () => Promise<void>;
};

type SyncTone = 'neutral' | 'success' | 'error';

export function useOptionsController({ tab, tr, openUrl, onAfterImportRestore }: UseOptionsControllerParams) {
  const [backupStatus, setBackupStatus] = useState<string | null>(null);
  const [backupStatusTone, setBackupStatusTone] = useState<SyncTone>('neutral');
  const [syncActivity, setSyncActivity] = useState<string | null>(null);
  const [driveConnected, setDriveConnected] = useState(false);
  const [entitlement, setEntitlement] = useState<EntitlementState>(() => getCachedEntitlement());
  const [billingStatusText, setBillingStatusText] = useState<string | null>(null);
  const [monetizationPlans, setMonetizationPlans] = useState<MonetizationPlan[]>([]);
  const [canManageBilling, setCanManageBilling] = useState(false);
  const [billingDiagnostics, setBillingDiagnostics] = useState<BillingDiagnostics | null>(null);

  async function refreshBillingCatalog(): Promise<void> {
    const plans = await listAvailablePlans();
    setMonetizationPlans(plans);
    const managementUrl = await getSubscriptionManagementUrl();
    setCanManageBilling(Boolean(managementUrl));
  }

  useEffect(() => {
    setDriveConnected(isGoogleDriveConnected());
  }, []);

  useEffect(() => {
    void (async () => {
      const next = await refreshEntitlementFromStore();
      setEntitlement(next);
      if (billingConfigured()) {
        await refreshBillingCatalog();
      }
    })();
  }, []);

  useEffect(() => {
    if (tab !== 'options' || !billingConfigured()) return;
    void refreshBillingCatalog();
  }, [tab]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let removed = false;
    let listenerHandle: { remove: () => Promise<void> } | null = null;

    void (async () => {
      const { App } = await import('@capacitor/app');
      listenerHandle = await App.addListener('appStateChange', ({ isActive }) => {
        if (!isActive || removed || !billingConfigured()) return;
        void (async () => {
          const next = await refreshEntitlementFromStore();
          if (removed) return;
          setEntitlement(next);
          await refreshBillingCatalog();
        })();
      });
    })();

    return () => {
      removed = true;
      void listenerHandle?.remove();
    };
  }, []);

  async function createBackup(passphrase: string): Promise<void> {
    setBackupStatus(null);
    setSyncActivity(tr('options.sync_working_action', { action: tr('options.backup_create') }));
    try {
      const bytes = await exportDbSnapshot();
      const payload = await createEncryptedBackup(bytes, passphrase);
      const stamp = new Date().toISOString().replace(/[:.]/g, '-');
      downloadBlob(`rethread-backup-${stamp}.json`, payload, 'application/json');
      setBackupStatus(tr('options.backup_done'));
      setBackupStatusTone('success');
    } catch (error) {
      const message = error instanceof Error ? error.message : tr('options.backup_failed');
      setBackupStatus(message);
      setBackupStatusTone('error');
    } finally {
      setSyncActivity(null);
    }
  }

  async function restoreBackup(file: File, passphrase: string): Promise<void> {
    setBackupStatus(null);
    setSyncActivity(tr('options.sync_working_action', { action: tr('options.backup_restore') }));
    try {
      const text = await file.text();
      const bytes = await decryptEncryptedBackup(text, passphrase);
      await importDbSnapshot(bytes);
      await onAfterImportRestore();
      setBackupStatus(tr('options.restore_done'));
      setBackupStatusTone('success');
    } catch (error) {
      const message = error instanceof Error ? error.message : tr('options.restore_failed');
      setBackupStatus(message);
      setBackupStatusTone('error');
    } finally {
      setSyncActivity(null);
    }
  }

  async function connectDriveAccount(): Promise<void> {
    setBackupStatus(null);
    setSyncActivity(tr('options.sync_working_action', { action: tr('options.drive_connect') }));
    try {
      await connectGoogleDrive();
      setDriveConnected(isGoogleDriveConnected());
      setBackupStatus(tr('options.drive_connected_status'));
      setBackupStatusTone('success');
    } catch (error) {
      const message = error instanceof Error ? error.message : tr('options.drive_connect_failed');
      setBackupStatus(message);
      setBackupStatusTone('error');
    } finally {
      setSyncActivity(null);
    }
  }

  function disconnectDriveAccount(): void {
    disconnectGoogleDrive();
    setDriveConnected(false);
    setBackupStatus(tr('options.drive_disconnected_status'));
    setBackupStatusTone('neutral');
    setSyncActivity(null);
  }

  async function uploadDriveBackupFile(passphrase: string): Promise<void> {
    setBackupStatus(null);
    setSyncActivity(tr('options.sync_working_action', { action: tr('options.drive_upload') }));
    try {
      const bytes = await exportDbSnapshot();
      const payload = await createEncryptedBackup(bytes, passphrase);
      await uploadBackupToGoogleDrive(payload);
      setDriveConnected(isGoogleDriveConnected());
      setBackupStatus(tr('options.drive_upload_done'));
      setBackupStatusTone('success');
    } catch (error) {
      const message = error instanceof Error ? error.message : tr('options.drive_upload_failed');
      setBackupStatus(message);
      setBackupStatusTone('error');
    } finally {
      setSyncActivity(null);
    }
  }

  async function restoreDriveBackupFile(passphrase: string): Promise<void> {
    setBackupStatus(null);
    setSyncActivity(tr('options.sync_working_action', { action: tr('options.drive_restore') }));
    try {
      const payload = await downloadLatestBackupFromGoogleDrive();
      const bytes = await decryptEncryptedBackup(payload, passphrase);
      await importDbSnapshot(bytes);
      await onAfterImportRestore();
      setDriveConnected(isGoogleDriveConnected());
      setBackupStatus(tr('options.drive_restore_done'));
      setBackupStatusTone('success');
    } catch (error) {
      const message = error instanceof Error ? error.message : tr('options.drive_restore_failed');
      setBackupStatus(message);
      setBackupStatusTone('error');
    } finally {
      setSyncActivity(null);
    }
  }

  async function checkBilling(): Promise<void> {
    if (!billingConfigured()) {
      setBillingStatusText(tr('options.billing_not_configured'));
      return;
    }
    setSyncActivity(tr('options.sync_working_action', { action: tr('options.billing_check') }));
    try {
      const next = await refreshEntitlementFromStore();
      setEntitlement(next);
      await refreshBillingCatalog();
      appendMonetizationEvent({
        category: 'billing',
        action: 'check_billing',
        status: next.source === 'play' ? 'success' : 'error',
        detail: `Billing check completed with tier ${next.tier} from ${next.source}.`
      });
      setBillingStatusText(next.source === 'play' ? tr('options.billing_check_done') : tr('options.billing_check_failed'));
    } catch {
      appendMonetizationEvent({ category: 'billing', action: 'check_billing', status: 'error', detail: 'Billing check failed.' });
      setBillingStatusText(tr('options.billing_check_failed'));
    } finally {
      setSyncActivity(null);
    }
  }

  async function restoreBilling(): Promise<void> {
    if (!billingConfigured()) {
      setBillingStatusText(tr('options.billing_not_configured'));
      return;
    }
    setSyncActivity(tr('options.sync_working_action', { action: tr('options.billing_restore') }));
    try {
      const next = await restoreEntitlementsFromStore();
      setEntitlement(next);
      await refreshBillingCatalog();
      appendMonetizationEvent({
        category: 'billing',
        action: 'restore_billing',
        status: next.source === 'play' ? 'success' : 'error',
        detail: `Restore completed with tier ${next.tier} from ${next.source}.`
      });
      setBillingStatusText(next.source === 'play' ? tr('options.billing_restore_done') : tr('options.billing_restore_failed'));
    } catch {
      appendMonetizationEvent({ category: 'billing', action: 'restore_billing', status: 'error', detail: 'Billing restore failed.' });
      setBillingStatusText(tr('options.billing_restore_failed'));
    } finally {
      setSyncActivity(null);
    }
  }

  async function buyPlan(productId: string): Promise<void> {
    if (!billingConfigured()) {
      setBillingStatusText(tr('options.billing_not_configured'));
      return;
    }
    setSyncActivity(tr('options.sync_working_action', { action: tr('options.billing_buy') }));
    try {
      appendMonetizationEvent({ category: 'billing', action: 'purchase_started', status: 'info', detail: `Started purchase for ${productId}.` });
      const result = await purchasePlan(productId);
      setEntitlement(result.state);
      await refreshBillingCatalog();
      appendMonetizationEvent({
        category: 'billing',
        action: 'purchase_finished',
        status: result.status === 'success' ? 'success' : result.status === 'canceled' ? 'info' : 'error',
        detail: `Purchase for ${productId} finished with status ${result.status} and tier ${result.state.tier}.`
      });
      setBillingStatusText(result.status === 'success' ? tr('options.billing_buy_done') : tr('options.billing_buy_failed'));
    } catch {
      appendMonetizationEvent({ category: 'billing', action: 'purchase_finished', status: 'error', detail: `Purchase for ${productId} failed unexpectedly.` });
      setBillingStatusText(tr('options.billing_buy_failed'));
    } finally {
      setSyncActivity(null);
    }
  }

  async function manageBilling(): Promise<void> {
    setSyncActivity(tr('options.sync_working_action', { action: tr('options.billing_manage') }));
    try {
      const url = await getSubscriptionManagementUrl();
      if (!url) {
        appendMonetizationEvent({ category: 'billing', action: 'manage_billing', status: 'error', detail: 'Subscription management URL is unavailable.' });
        setBillingStatusText(tr('options.billing_manage_unavailable'));
        return;
      }
      await openUrl(url);
      appendMonetizationEvent({ category: 'billing', action: 'manage_billing', status: 'success', detail: 'Opened subscription management URL.' });
      setBillingStatusText(tr('options.billing_manage_opened'));
    } finally {
      setSyncActivity(null);
    }
  }

  async function runBillingDiagnostics(): Promise<void> {
    setSyncActivity(tr('options.sync_working_action', { action: tr('options.billing_diagnostics_run') }));
    try {
      const snapshot = await getBillingDiagnostics();
      setBillingDiagnostics(snapshot);
      if (snapshot.error) {
        appendMonetizationEvent({ category: 'billing', action: 'run_diagnostics', status: 'error', detail: snapshot.error });
        setBillingStatusText(snapshot.error);
        return;
      }
      appendMonetizationEvent({
        category: 'billing',
        action: 'run_diagnostics',
        status: 'success',
        detail: `Diagnostics completed with ${snapshot.availablePackageCount} package(s) and ${snapshot.recentEvents.length} recent event(s).`
      });
      setBillingStatusText(tr('options.billing_diagnostics_done'));
    } catch {
      appendMonetizationEvent({ category: 'billing', action: 'run_diagnostics', status: 'error', detail: 'Billing diagnostics failed.' });
      setBillingStatusText(tr('options.billing_diagnostics_failed'));
    } finally {
      setSyncActivity(null);
    }
  }

  function clearBillingDiagnosticsEvents(): void {
    clearMonetizationEvents();
    setBillingDiagnostics((prev) => (prev ? { ...prev, recentEvents: [] } : prev));
    appendMonetizationEvent({ category: 'billing', action: 'clear_event_log', status: 'info', detail: 'Monetization event log cleared.' });
  }

  function resetOptionsState(): void {
    setBackupStatus(null);
    setBackupStatusTone('neutral');
    setSyncActivity(null);
    setDriveConnected(false);
    setEntitlement({ tier: 'free', source: 'none', updatedAtMs: 0 });
    setBillingStatusText(null);
    setMonetizationPlans([]);
    setCanManageBilling(false);
    setBillingDiagnostics(null);
  }

  return {
    backupStatus,
    setBackupStatus,
    backupStatusTone,
    setBackupStatusTone,
    syncActivity,
    setSyncActivity,
    driveConnected,
    driveConfigured: hasGoogleDriveClientId(),
    entitlement,
    billingConfigured: billingConfigured(),
    billingStatusText,
    setBillingStatusText,
    monetizationPlans,
    canManageBilling,
    billingDiagnostics,
    clearBillingDiagnosticsEvents,
    createBackup,
    restoreBackup,
    connectDrive: connectDriveAccount,
    disconnectDrive: disconnectDriveAccount,
    uploadDriveBackup: uploadDriveBackupFile,
    restoreDriveBackup: restoreDriveBackupFile,
    checkBilling,
    restoreBilling,
    buyPlan,
    manageBilling,
    runBillingDiagnostics,
    resetOptionsState
  };
}
